import { WARDROBE } from "./wardrobe";
import { SKIN_HUMAN, SKIN_AGENT, HAIR_COLORS, CLOTH_COLORS } from "./palettes";

import type { AvatarLook } from "@/shared/types";
import type { Material } from "./palettes";
import type { WardrobeItem } from "./wardrobe";

/** The expression sheets vendored from LPC. */
export type FaceId = "neutral" | "happy" | "anger" | "sad" | "closed" | "shock" | "blush";

/** One sprite sheet to paint, already recoloured to `ramp`. `z` is paint
    order across all layers, not per slot. */
export interface SpriteLayer {
  key: string;
  src: string;
  material: Material;
  ramp: string;
  z: number;
}

/** One dial in the Settings customizer. `options` takes `human` because the
    skin dial draws from a different set for agents. */
interface Dial {
  key: keyof AvatarLook;
  label: string;
  options: (human?: boolean) => Array<{ id: string; label: string }>;
}

/* ----------------------------------------------------------------------------
   Identity — seed in, character out.

   Every avatar is derived from its seed, so an agent looks the same in the
   sidebar, the chat header and the office without anything storing a picture.
   A saved `avatar` object overrides the derivation field by field, which is
   what the Settings customizer writes.

   Agents and humans draw from disjoint skin ramps. That is the primary tell:
   nothing with human skin is an agent, and nothing green is a person. The
   frame shape (circle for people, squircle for agents) carries the same signal
   at sizes where six pixels of hue can't.
---------------------------------------------------------------------------- */

function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// Independent draws from one seed — using `h >>> n` for each field would
// correlate them, so each field re-hashes with its own salt.
const pick = <T,>(seed: string, salt: string, list: T[]): T => list[hash(`${seed}:${salt}`) % list.length];

/** Moods the app already computes → the expression sheets we vendored. */
const MOOD_TO_FACE: Record<string, FaceId> = {
  happy: "happy",
  angry: "anger",
  sad: "sad",
  sleeping: "closed",
  shock: "shock",
  blush: "blush",
};

export const faceForMood = (mood: string | null | undefined): FaceId => MOOD_TO_FACE[mood!] || "neutral";

/** The dials Settings exposes, in display order. */
export const CUSTOMIZABLE: Dial[] = [
  { key: "head", label: "Head", options: () => WARDROBE.heads.map((h) => ({ id: h.id, label: h.name })) },
  { key: "hair", label: "Hair", options: () => WARDROBE.hair.map((h) => ({ id: h.id, label: h.name })) },
  { key: "hairColor", label: "Hair colour", options: () => HAIR_COLORS.map((c) => ({ id: c, label: label(c) })) },
  { key: "skin", label: "Skin", options: (human) => (human ? SKIN_HUMAN : SKIN_AGENT).map((c) => ({ id: c, label: label(c) })) },
];

const label = (id: string) => id.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * Resolve a seed (plus any saved overrides) into a full character.
 */
export function resolveAvatar(
  seed: string = "agent",
  { human = false, override = null }: { human?: boolean; override?: Partial<AvatarLook> | null } = {}
): AvatarLook {
  const s = String(seed);
  const skins = human ? SKIN_HUMAN : SKIN_AGENT;
  const base = {
    head: pick(s, "head", WARDROBE.heads).id,
    hair: pick(s, "hair", WARDROBE.hair).id,
    hairColor: pick(s, "haircolor", HAIR_COLORS),
    skin: pick(s, "skin", skins),
    // Clothing only shows in the full-body city view, but it's resolved here so
    // one character is one object wherever it's drawn.
    top: pick(s, "top", WARDROBE.torso).id,
    topColor: pick(s, "topcolor", CLOTH_COLORS),
    bottom: pick(s, "bottom", WARDROBE.legs).id,
    bottomColor: pick(s, "bottomcolor", CLOTH_COLORS),
    shoes: pick(s, "shoes", WARDROBE.feet).id,
    shoesColor: pick(s, "shoescolor", CLOTH_COLORS),
  };
  const merged = { ...base, ...(override || {}) };

  // A saved skin from the wrong set would break the agent/human distinction —
  // e.g. an agent customised while `human` was momentarily true. Snap it back.
  if (!skins.includes(merged.skin)) merged.skin = base.skin;
  return merged;
}

/** A fresh random character. Used by the shuffle button. */
export function shuffleSeed(): string {
  return `s${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/**
 * The sprite layers for one character, already in paint order.
 *
 * `headOnly` drops the body sheet. The head sheet already carries the neck, so
 * the body only contributes shoulders — which, with no shirt layer, read as a
 * naked torso poking into the bottom of a portrait.
 */
export function layersFor(
  avatar: AvatarLook,
  { face = "neutral", animation = "idle", headOnly = true }:
    { face?: FaceId; animation?: string; headOnly?: boolean } = {}
): SpriteLayer[] {
  const head = WARDROBE.heads.find((h) => h.id === avatar.head) || WARDROBE.heads[0];
  const hair = WARDROBE.hair.find((h) => h.id === avatar.hair);
  const url = (dir: string) => `${process.env.PUBLIC_URL || ""}/lpc/${dir}${animation}.png`;

  const layers: SpriteLayer[] = [
    { key: "head", src: url(head.dir), material: "skin", ramp: avatar.skin, z: 100 },
    { key: "face", src: url(`head/faces/${head.faceDir}/${face}/`), material: "skin", ramp: avatar.skin, z: 101 },
  ];
  if (!headOnly) {
    const body = WARDROBE.body[head.id] || WARDROBE.body.male;
    layers.push({ key: "body", src: url(body), material: "skin", ramp: avatar.skin, z: 10 });

    // Clothing carries its own zPos because the order isn't fixed: boots sit
    // over trousers (25 > 20), shoes under them (15 < 20).
    const worn: Array<[string, WardrobeItem[], string, string]> = [
      ["shoes", WARDROBE.feet, avatar.shoes, avatar.shoesColor],
      ["bottom", WARDROBE.legs, avatar.bottom, avatar.bottomColor],
      ["top", WARDROBE.torso, avatar.top, avatar.topColor],
    ];
    for (const [key, set, id, color] of worn) {
      const item = set.find((x) => x.id === id) || set[0];
      if (!item) continue;
      layers.push({ key, src: url(item.dir), material: "cloth", ramp: color, z: item.zPos });
    }
  }
  if (hair) layers.push({ key: "hair", src: url(hair.dir), material: "hair", ramp: avatar.hairColor, z: hair.zPos ?? 120 });
  return layers.sort((a, b) => a.z - b.z);
}
